import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import LayoutSkeletons from './LayoutSkeletons';
import LayoutCarousel from './LayoutCarousel';
import LayoutCarouselStatic from './LayoutCarouselStatic';
import LayoutDining from './LayoutDining';
import LayoutOffer from './LayoutOffer';
import LayoutCredit from './LayoutCredit';
import LayoutMenus from './LayoutMenus';
import LayoutStaticText from './LayoutStaticText';
import { convertToPlainText, handleMktAnalytics } from '../AmexUtils';
import { CTA_EVENT_NAME } from '../constants';
import './AmexTabbedCarousel.scss';

const MAX_TABS = 5;

let tabScrollTimeout;

const AmexTabbedCarousel = (
  {
    baseRequestUrl,
    awsFolderName,
    carouselConfig,
    handleTagging,
    hash,
    matchMedia,
    eventContents,
    venueContent,
    eventContext,
    Analytics,
    isPageReady,
    hasStickyHeader,
  }
) => {
  if (!carouselConfig) return false;
  const {
    id,
    initial_active_index: initialActiveIndex,
    loading_timeout: loadingTimeout,
    section_intro: sectionIntro,
    section_title: sectionTitle,
    sections,
    event_tag: eventTag,
    action_name: tabActionName,
    loading_icon: loadingIconName,
  } = carouselConfig;

  if (!sections || sections.length === 0) return false;
  const updatedSections = sections.filter((tab) => !(tab.hide_section || !tab.title || !tab.content || Object.keys(tab.content).length === 0))
    .sort((a, b) => {
      if (a?.order_id && b?.order_id) return a.order_id > b.order_id ? 1 : -1;
      return a?.order_id ? -1 : 1;
    })
    .slice(0, MAX_TABS);
  if (updatedSections.length === 0) return false;

  const initialIndex = initialActiveIndex ? initialActiveIndex - 1 : 0;
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(false);
  const activePanelContainerTimeout = useRef(null);
  const activeTabTimeout = useRef(null);
  const handleClickTimeout = useRef(null);
  const tabContainerRefs = useRef(updatedSections.map(() => React.createRef()));
  const activePanelContainerRefs = useRef(updatedSections.map(() => React.createRef()));
  const carouselContainer = useRef(null);
  const tabIds = updatedSections.map((section) => section.id);

  const sanitizedSectionTitle = sanitizeString(sectionTitle);
  const sanitizedSectionIntro = sanitizeString(sectionIntro);

  const handleMobileTabScroll = (index) => {
    const offsetTopValue = hasStickyHeader ? -90 : -50;
    if (activePanelContainerRefs?.current) {
      const activePanel = activePanelContainerRefs.current[index]?.current;
      if (activePanelContainerTimeout.current) clearTimeout(activePanelContainerTimeout.current);
      // scroll to the active panel
      activePanelContainerTimeout.current = setTimeout(() => {
        window.scrollTo(0, activePanel.offsetTop + offsetTopValue);
      }, 1500);
      if (tabContainerRefs?.current) {
        const activeTab = tabContainerRefs.current[index]?.current;
        if (activeTab) {
          if (activeTabTimeout.current) clearTimeout(activeTabTimeout.current);
          // scroll horizontally to the active tab(button)
          activeTabTimeout.current = setTimeout(() => {
            activeTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            activeTab.focus({ preventScroll: true });
          }, 1100);
        }
      }
    }
  };

  const handleDesktopTabScroll = () => {
    const offsetTopValue = hasStickyHeader ? -90 : 0;
    window.scrollTo(0, carouselContainer?.current.offsetTop + offsetTopValue);
  };

  const handleClickWithTagging = (item) => {
    if (tabActionName) {
      const specificParam = {
        tab_item: convertToPlainText(item.title),
        action_name: tabActionName,
      };
      handleTagging(eventTag, specificParam);
    } else {
      const specificParamsAmplitude = {
        tab_item: convertToPlainText(item.title),
        action_name: 'Tab Click',
      };
      handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, specificParamsAmplitude);
    }
  };

  const handleClick = (index, item) => {
    setIsLoading(true);
    setActiveIndex(index);
    if (handleClickTimeout.current) clearTimeout(handleClickTimeout.current);
    handleClickTimeout.current = setTimeout(() => {
      setIsLoading(false);
    }, loadingTimeout);
    handleClickWithTagging(item);
  };

  const handleKeyDown = (keyEvent) => {
    const count = updatedSections.length;
    const nextTab = () => setActiveIndex((activeIndex + 1) % count);
    const prevTab = () => setActiveIndex((activeIndex - 1 + count) % count);
    const firstTab = () => setActiveIndex(0);
    const lastTab = () => setActiveIndex(count - 1);
    const keyMap = {
      ArrowRight: nextTab,
      ArrowLeft: prevTab,
      Home: firstTab,
      End: lastTab,
    };

    const action = keyMap[keyEvent.key];
    if (action) {
      action();
    }
  };

  const renderLayout = (layout, subLayout, content, title) => {
    switch (layout) {
      case 'templateG':
        return (
          <LayoutMenus
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            content={content}
            Analytics={Analytics}
            tabHeading={title}
            matchMedia={matchMedia}
          />
        );
      case 'templateF':
        return (
          <LayoutCredit
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            content={content}
            tabHeading={title}
            matchMedia={matchMedia}
            Analytics={Analytics}
          />
        );
      case 'templateE':
        return (
          <LayoutStaticText
            content={content}
            tabHeading={title}
          />
        );
      case 'templateD':
        return (
          <LayoutCarouselStatic
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            content={content}
            Analytics={Analytics}
            matchMedia={matchMedia}
            subLayout={subLayout}
            tabHeading={title}
          />
        );
      case 'templateC':
        return (
          <LayoutCarousel
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            content={content}
            handleTagging={handleTagging}
            Analytics={Analytics}
            eventContext={eventContext}
            eventsContent={eventContents}
            venueContent={venueContent}
            matchMedia={matchMedia}
            tabHeading={title}
          />
        );
      case 'templateB':
        return (
          <LayoutDining
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            content={content}
            tabHeading={title}
            Analytics={Analytics}
          />
        );
      case 'templateA':
        return (
          <LayoutOffer
            awsFolderName={awsFolderName}
            baseRequestUrl={baseRequestUrl}
            content={content}
            tabHeading={title}
            Analytics={Analytics}
          />
        );
      default:
        return null;
    }
  };

  const renderContent = (view) => updatedSections
    .map((item, index) => {
      const {
        bg_color: tabBgColor,
        font_color: tabFontColor,
        bg_image: tabBgImage,
        bg_gradient: tabBgGradient,
        thumb_image: tabThumbImage,
        title,
        subtitle,
        layout,
        sub_layout: subLayout = '',
        content,
      } = item;

      const sanitizedTitle = sanitizeString(title);
      const sanitizedSubtitle = sanitizeString(subtitle);

      if (layout === 'templateC' && !eventContents) return null;
      if (layout === 'templateD' && !content?.carousel) return null;
      if (layout === 'templateE' && (!content?.copy)) return null;
      if (layout === 'templateG' && (!content?.menu_config || content?.menu_config.length === 0)) return null;
      const bgImage = `url(${baseRequestUrl}${awsFolderName}/carouselImages/tabBgImages/${tabBgImage})`;
      const thumbImage = `${baseRequestUrl}${awsFolderName}/carouselImages/${tabThumbImage}`;
      const loadingIconSrc = `${baseRequestUrl}${awsFolderName}/carouselImages/${loadingIconName}`;
      const loadingIcon = loadingIconName ? loadingIconSrc : '';
      const tabBackground = tabBgImage ? bgImage : tabBgGradient;
      const isActive = activeIndex === index;

      const style = {
        backgroundImage: tabBackground && tabBackground,
        backgroundColor: tabBgColor && tabBgColor,
      };

      switch (view) {
        case 'mobilePanel':
          return (
            <div
              role="tabpanel"
              key={`tabpanel-${title}`}
              hidden={!isActive}
              ref={activePanelContainerRefs.current[index]}
              className={`AmexTabbedCarousel__tabpanel ${isActive ? 'active' : ''}`}
              style={{ backgroundColor: tabBgColor }}
              aria-labelledby={item.id}
              tabIndex="0"
              id={`panel-${index}`}
            >
              <div className="AmexTabbedCarousel__content">
                {renderLayout(layout, subLayout, content, title)}
              </div>
            </div>
          );
        case 'mobileTabs':
          return (
            <Button
              variant="no-style"
              type="button"
              role="tab"
              id={item.id}
              key={`tab-${item.title}`}
              ref={tabContainerRefs.current[index]}
              aria-controls={`panel-${index}`}
              aria-selected={isActive}
              className={`AmexTabbedCarousel__tab ${isActive ? 'active' : ''}`}
              onClick={() => {
                setActiveIndex(index);
                handleMobileTabScroll(index);
                handleClickWithTagging(item);
              }}
              onKeyDown={handleKeyDown}
              tabIndex={activeIndex === index ? 0 : -1}
              dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                __html: sanitizedTitle,
              }}
            />
          );
        case 'desktop':
        default:
          return (
            <li
              key={`panel-${item.title}`}
              className={`AmexTabbedCarousel__accordion--panel ${isActive ? 'active' : ''}`}
              {...{ style }}
            >
              <h3
                className={`AmexTabbedCarousel__accordion--title ${isActive ? 'sr-only' : ''}`}
              >
                <Button
                  variant="no-style"
                  type="button"
                  id={item.id}
                  aria-controls={`AmexTabbedAccordionPanel${index}`}
                  className="AmexTabbedCarousel__accordion--button"
                  aria-expanded={isActive}
                  onClick={() => handleClick(index, item)}
                  style={{ color: tabFontColor && tabFontColor }}
                >
                  <span className="AmexTabbedCarousel__accordion--button--inner">
                    { thumbImage && (
                      <img
                        className="AmexTabbedCarousel__accordion--image"
                        src={thumbImage}
                        alt=""
                      />
                    )}
                    <span
                      className="AmexTabbedCarousel__accordion--button--title"
                      dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                        __html: sanitizedTitle,
                      }}
                    />
                    {subtitle && (
                      <span
                        className="AmexTabbedCarousel__accordion--button--subtitle"
                        dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                          __html: sanitizedSubtitle,
                        }}
                      />
                    )}
                  </span>
                </Button>
              </h3>
              <div
                id={`AmexTabbedAccordionPanel${index}`}
                role="region"
                className={`AmexTabbedCarousel__accordion--content ${activeIndex === index ? 'active' : ''}`}
                aria-hidden={activeIndex !== index}
                aria-labelledby={item.id}
                aria-busy={isLoading}
              >
                <span className="sr-only AmexTabbedCarousel__accordion--loaded" aria-live="polite" role="status">
                  {isLoading ? 'Loading...' : 'Loaded'}
                </span>
                {isLoading ? (
                  <LayoutSkeletons layout={layout} icon={loadingIcon} />
                ) : renderLayout(layout, subLayout, content, title)}
              </div>
            </li>
          );
      }
    });

  const renderMobile = () => (
    <>
      <div className="AmexTabbedCarousel__tabswrapper">
        <div
          role="tablist"
          className="AmexTabbedCarousel__tabs"
          id="carouselTabs"
          aria-labelledby={id}
        >
          {renderContent('mobileTabs')}
        </div>
      </div>
      {renderContent('mobilePanel')}
    </>
  );

  const renderDesktop = () => (
    <div ref={carouselContainer}>
      <ul className="AmexTabbedCarousel__accordion">
        {renderContent('desktop')}
      </ul>
    </div>
  );

  useEffect(() => {
    if (isPageReady) {
      if (hash && tabIds.includes(hash)) {
        setActiveIndex(tabIds.indexOf(hash));
        if (matchMedia.xl) {
          tabScrollTimeout = setTimeout(() => handleDesktopTabScroll(), 500);
        } else {
          handleMobileTabScroll(tabIds.indexOf(hash));
        }
      }
    }
    return () => {
      if (tabScrollTimeout) clearTimeout(tabScrollTimeout);
    };
  }, [hash, isPageReady]);

  return (
    <section className="AmexTabbedCarousel">
      <div className="container">
        {sectionTitle && (
          <h2
            id={id}
            className="AmexTabbedCarousel__title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSectionTitle,
            }}
          />
        )}
        {sectionIntro && (
          <p
            className="AmexTabbedCarousel__intro"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitizedSectionIntro,
            }}
          />
        )}
        {matchMedia.xl ? renderDesktop() : renderMobile()}
      </div>
    </section>
  );
};

AmexTabbedCarousel.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  carouselConfig: PropTypes.shape({
    id: PropTypes.string.isRequired,
    initial_active_index: PropTypes.number.isRequired,
    loading_timeout: PropTypes.number.isRequired,
    section_intro: PropTypes.string,
    section_title: PropTypes.string.isRequired,
    sections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    event_tag: PropTypes.string,
    action_name: PropTypes.string,
    loading_icon: PropTypes.string,
  }).isRequired,
  handleTagging: PropTypes.func,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }),
  hash: PropTypes.string.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  eventContents: PropTypes.shape({}),
  venueContent: PropTypes.shape({}),
  eventContext: PropTypes.string,
  isPageReady: PropTypes.bool.isRequired,
  hasStickyHeader: PropTypes.bool,
};

AmexTabbedCarousel.defaultProps = {
  eventContents: null,
  venueContent: null,
  eventContext: '',
  handleTagging: null,
  Analytics: {
    event: () => {},
  },
  hasStickyHeader: false,
};

export default AmexTabbedCarousel;
